import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, ListGroup, Spinner } from 'react-bootstrap';
import { Search } from 'lucide-react';
import { api } from '../../utils/apiClient';

const COUNTRIES = [
  'United States',
  'United Kingdom',
  'India',
  'Canada',
  'Australia',
  'Germany',
  'France',
  'United Arab Emirates',
  'Singapore',
  'Other',
];

const emptyAddress = () => ({
  street: '',
  city: '',
  state: '',
  country: '',
  zip: '',
});

const CustomerLookup = ({ value, onChange }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]);

  useEffect(() => {
    api
      .customerGroups()
      .then((data) => setGroups(data?.groups || []))
      .catch(() => setGroups([]));
  }, []);

  const search = useCallback(async (term) => {
    if (!term.trim()) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const data = await api.searchCustomers(term);
      setResults(data?.customers || []);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleQueryChange = (e) => {
    const term = e.target.value;
    setQuery(term);
    search(term);
  };

  const selectCustomer = (customer) => {
    onChange?.({
      name: customer.name || '',
      email: customer.email || '',
      company: customer.company || '',
      phone: customer.phone || '',
      customerGroupId: customer.customerGroupId || '',
      address: {
        ...emptyAddress(),
        ...(customer.address || {}),
      },
    });
    setQuery('');
    setResults([]);
  };

  const setField = (field, fieldValue) => {
    onChange?.({ ...value, [field]: fieldValue });
  };

  const setAddress = (field, fieldValue) => {
    onChange?.({
      ...value,
      address: { ...emptyAddress(), ...(value?.address || {}), [field]: fieldValue },
    });
  };

  const address = value?.address || emptyAddress();

  return (
    <div>
      <Form.Group className="mb-3">
        <Form.Label>{t('customerLookup.searchCustomers')}</Form.Label>
        <div className="position-relative">
          <Form.Control
            placeholder={t('customerLookup.searchPlaceholder')}
            value={query}
            onChange={handleQueryChange}
          />
          <Search size={16} className="position-absolute text-muted" style={{ right: 12, top: 10 }} />
        </div>
        {loading && (
          <div className="mt-2">
            <Spinner size="sm" animation="border" />
          </div>
        )}
        {results.length > 0 && (
          <ListGroup className="mt-2 shadow-sm">
            {results.map((c) => (
              <ListGroup.Item
                key={c.ecwidCustomerId || c.email}
                action
                onClick={() => selectCustomer(c)}
              >
                <div className="fw-semibold">{c.name}</div>
                <div className="small text-muted">{c.email}</div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}
      </Form.Group>

      <div className="row g-3">
        <div className="col-md-6">
          <Form.Group>
            <Form.Label>{t('customerLookup.nameRequired')}</Form.Label>
            <Form.Control value={value?.name || ''} onChange={(e) => setField('name', e.target.value)} required />
          </Form.Group>
        </div>
        <div className="col-md-6">
          <Form.Group>
            <Form.Label>{t('customerLookup.email')}</Form.Label>
            <Form.Control type="email" value={value?.email || ''} onChange={(e) => setField('email', e.target.value)} />
          </Form.Group>
        </div>
        <div className="col-md-6">
          <Form.Group>
            <Form.Label>{t('customerLookup.company')}</Form.Label>
            <Form.Control value={value?.company || ''} onChange={(e) => setField('company', e.target.value)} />
          </Form.Group>
        </div>
        <div className="col-md-6">
          <Form.Group>
            <Form.Label>{t('customerLookup.phone')}</Form.Label>
            <Form.Control value={value?.phone || ''} onChange={(e) => setField('phone', e.target.value)} />
          </Form.Group>
        </div>
        <div className="col-12">
          <Form.Group>
            <Form.Label>{t('customerLookup.streetAddress')}</Form.Label>
            <Form.Control value={address.street || ''} onChange={(e) => setAddress('street', e.target.value)} />
          </Form.Group>
        </div>
        <div className="col-md-4">
          <Form.Group>
            <Form.Label>{t('customerLookup.city')}</Form.Label>
            <Form.Control value={address.city || ''} onChange={(e) => setAddress('city', e.target.value)} />
          </Form.Group>
        </div>
        <div className="col-md-4">
          <Form.Group>
            <Form.Label>{t('customerLookup.state')}</Form.Label>
            <Form.Control value={address.state || ''} onChange={(e) => setAddress('state', e.target.value)} />
          </Form.Group>
        </div>
        <div className="col-md-4">
          <Form.Group>
            <Form.Label>{t('customerLookup.zip')}</Form.Label>
            <Form.Control value={address.zip || ''} onChange={(e) => setAddress('zip', e.target.value)} />
          </Form.Group>
        </div>
        <div className="col-md-6">
          <Form.Group>
            <Form.Label>{t('customerLookup.country')}</Form.Label>
            <Form.Select value={address.country || ''} onChange={(e) => setAddress('country', e.target.value)}>
              <option value="">{t('customerLookup.selectCountry')}</option>
              {COUNTRIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </div>
        <div className="col-md-6">
          <Form.Group>
            <Form.Label>{t('customerLookup.customerGroup')}</Form.Label>
            <Form.Select
              value={value?.customerGroupId || ''}
              onChange={(e) => setField('customerGroupId', e.target.value)}
            >
              <option value="">{t('customerLookup.selectCustomerGroup')}</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </div>
      </div>
    </div>
  );
};

export default CustomerLookup;
export { emptyAddress };
